import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, FileText, Plus, Search, X, ArrowUpDown, Bookmark, Star } from "lucide-react";

const RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const SORT_OPTIONS = [
  { value: "updated_desc", label: "Recently updated" },
  { value: "updated_asc", label: "Oldest updated" },
  { value: "name_asc", label: "Candidate A → Z" },
  { value: "name_desc", label: "Candidate Z → A" },
];

export default function Library() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cv");
      setItems(r.data.items || []);
    } catch {
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const loadViews = async () => {
    try {
      const r = await api.get("/views");
      setViews(r.data.items || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    loadViews();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this CV?")) return;
    try {
      await api.delete(`/cv/${id}`);
      setItems((it) => it.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cutoff =
      range === "all"
        ? null
        : Date.now() - Number(range) * 24 * 60 * 60 * 1000;
    let out = items.filter((it) => {
      const d = it.data || {};
      if (q) {
        const hay = [
          d.full_name,
          d.title,
          d.location,
          d.nationality,
          d.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (cutoff !== null) {
        const t = new Date(it.updated_at).getTime();
        if (isNaN(t) || t < cutoff) return false;
      }
      return true;
    });
    out.sort((a, b) => {
      const nameA = (a.data?.full_name || "").toLowerCase();
      const nameB = (b.data?.full_name || "").toLowerCase();
      const tA = new Date(a.updated_at).getTime() || 0;
      const tB = new Date(b.updated_at).getTime() || 0;
      switch (sort) {
        case "updated_asc":
          return tA - tB;
        case "name_asc":
          return nameA.localeCompare(nameB);
        case "name_desc":
          return nameB.localeCompare(nameA);
        case "updated_desc":
        default:
          return tB - tA;
      }
    });
    return out;
  }, [items, query, range, sort]);

  const clearFilters = () => {
    setQuery("");
    setRange("all");
    setSort("updated_desc");
    setActiveView(null);
  };

  const applyView = (v) => {
    setQuery(v.query || "");
    setRange(v.range || "all");
    setSort(v.sort || "updated_desc");
    setActiveView(v.id);
  };

  const saveView = async () => {
    if (!viewName.trim()) return toast.error("Give this view a name");
    try {
      const r = await api.post("/views", {
        name: viewName.trim(),
        query,
        range,
        sort,
      });
      setViews((v) => [...v, r.data]);
      setActiveView(r.data.id);
      setViewName("");
      setSaveOpen(false);
      toast.success("View saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const deleteView = async (vid) => {
    try {
      await api.delete(`/views/${vid}`);
      setViews((v) => v.filter((x) => x.id !== vid));
      if (activeView === vid) setActiveView(null);
      toast.success("View deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtersActive = query.trim() || range !== "all" || sort !== "updated_desc";

  return (
    <div>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">TRAM · CV Library</div>
          <h1 className="font-serif-display text-4xl font-semibold text-[#0f1c3f]">
            Saved candidate CVs
          </h1>
        </div>
        <Button
          onClick={() => nav("/dashboard")}
          className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
          data-testid="new-cv-btn"
        >
          <Plus size={14} className="mr-1" /> New CV
        </Button>
      </div>

      <div className="mb-5 bg-white border border-[#e2ddd3] rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_200px_220px_auto_auto] gap-3 items-center">
        <div className="relative">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6b5d] pointer-events-none"
          />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveView(null);
            }}
            placeholder="Search by name, title, location…"
            className="pl-9 bg-white border-[#cfc8ba]"
            data-testid="library-search-input"
          />
          {query && (
            <button
              onClick={() => {
                setQuery("");
                setActiveView(null);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7a6b5d] hover:text-[#0f1c3f]"
              data-testid="library-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <Select
          value={range}
          onValueChange={(v) => {
            setRange(v);
            setActiveView(null);
          }}
        >
          <SelectTrigger className="border-[#cfc8ba] bg-white" data-testid="library-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sort}
          onValueChange={(v) => {
            setSort(v);
            setActiveView(null);
          }}
        >
          <SelectTrigger className="border-[#cfc8ba] bg-white" data-testid="library-sort-select">
            <ArrowUpDown size={13} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          className="border-[#cfc8ba]"
          onClick={() => setSaveOpen(true)}
          disabled={!filtersActive}
          data-testid="library-save-view"
        >
          <Bookmark size={13} className="mr-1" /> Save view
        </Button>

        <Button
          variant="outline"
          className="border-[#cfc8ba]"
          onClick={clearFilters}
          disabled={!filtersActive && !activeView}
          data-testid="library-clear-filters"
        >
          Reset
        </Button>
      </div>

      {views.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="library-saved-views">
          <span className="overline self-center mr-1">Saved views</span>
          {views.map((v) => (
            <div
              key={v.id}
              className={`group inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors ${
                activeView === v.id
                  ? "bg-[#0f1c3f] text-white border-[#0f1c3f]"
                  : "bg-white text-[#0f1c3f] border-[#cfc8ba] hover:bg-[#faf8f5]"
              }`}
              data-testid={`saved-view-${v.id}`}
            >
              <button
                onClick={() => applyView(v)}
                className="flex items-center gap-1.5"
              >
                <Star size={11} className={activeView === v.id ? "fill-[#c5a880] text-[#c5a880]" : ""} />
                <span className="font-medium">{v.name}</span>
              </button>
              <button
                onClick={() => deleteView(v.id)}
                className={`ml-1 rounded-full p-0.5 ${
                  activeView === v.id
                    ? "hover:bg-white/10"
                    : "hover:bg-[#e2ddd3] text-[#7a6b5d]"
                }`}
                data-testid={`saved-view-delete-${v.id}`}
                title="Delete view"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <div className="mb-3 text-[13px] text-[#7a6b5d]" data-testid="library-result-count">
          Showing <span className="text-[#0f1c3f] font-semibold">{filtered.length}</span> of{" "}
          {items.length} CV{items.length === 1 ? "" : "s"}
        </div>
      )}

      {loading ? (
        <div className="text-[#5a6578]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#cfc8ba] rounded-2xl p-16 text-center">
          <FileText className="mx-auto mb-3 text-[#0f1c3f]" size={32} />
          <div className="font-serif-display text-2xl text-[#0f1c3f]">No CVs yet</div>
          <p className="text-sm text-[#5a6578] mt-1">
            Upload your first candidate CV to see it here.
          </p>
          <Button
            onClick={() => nav("/dashboard")}
            className="mt-5 bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
          >
            Upload a CV
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#cfc8ba] rounded-2xl p-16 text-center">
          <Search className="mx-auto mb-3 text-[#0f1c3f]" size={28} />
          <div className="font-serif-display text-2xl text-[#0f1c3f]">No matches</div>
          <p className="text-sm text-[#5a6578] mt-1">
            Try a different search or date range.
          </p>
          <Button
            onClick={clearFilters}
            variant="outline"
            className="mt-5 border-[#cfc8ba]"
          >
            Reset filters
          </Button>
        </div>
      ) : (
        <div
          className="bg-white border border-[#e2ddd3] rounded-xl overflow-hidden"
          data-testid="cv-library-table"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[#e2ddd3]">
                <th className="px-6 py-3 overline">Candidate</th>
                <th className="px-6 py-3 overline">Title</th>
                <th className="px-6 py-3 overline">Location</th>
                <th className="px-6 py-3 overline">Updated</th>
                <th className="px-6 py-3 overline text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr
                  key={it.id}
                  className="border-b border-[#e2ddd3] last:border-0 hover:bg-[#faf8f5]"
                  data-testid={`cv-row-${it.id}`}
                >
                  <td className="px-6 py-4 font-semibold text-[#0f1c3f]">
                    {it.data?.full_name || "Untitled candidate"}
                  </td>
                  <td className="px-6 py-4 text-[#2c3540]">
                    {it.data?.title || "—"}
                  </td>
                  <td className="px-6 py-4 text-[#5a6578]">
                    {it.data?.location || "—"}
                  </td>
                  <td className="px-6 py-4 text-[#5a6578] font-mono-tram text-xs">
                    {new Date(it.updated_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => nav(`/editor/${it.id}`)}
                        className="border-[#cfc8ba]"
                        data-testid={`edit-cv-${it.id}`}
                      >
                        <Pencil size={13} className="mr-1" /> Edit
                      </Button>
                      <button
                        onClick={() => del(it.id)}
                        className="p-2 rounded-md border border-[#e2ddd3] text-[#c62828] hover:bg-[#faf8f5]"
                        data-testid={`delete-cv-${it.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0f1c3f]">
              Save this view
            </DialogTitle>
            <DialogDescription>
              Save the current search, date range and sort as a named tab you can pin above
              the library.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="overline">View name</Label>
            <Input
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="e.g. Cape Town Hospitality"
              className="mt-1 bg-white border-[#cfc8ba]"
              data-testid="save-view-name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") saveView();
              }}
            />
            <div className="mt-4 rounded-md bg-[#faf8f5] border border-[#e2ddd3] p-3 text-xs text-[#5a6578] space-y-1">
              <div>
                <span className="overline mr-2">Search</span>
                {query || <span className="italic text-[#8a94a6]">none</span>}
              </div>
              <div>
                <span className="overline mr-2">Range</span>
                {RANGE_OPTIONS.find((o) => o.value === range)?.label}
              </div>
              <div>
                <span className="overline mr-2">Sort</span>
                {SORT_OPTIONS.find((o) => o.value === sort)?.label}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveOpen(false)}
              className="border-[#cfc8ba]"
            >
              Cancel
            </Button>
            <Button
              onClick={saveView}
              className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
              data-testid="save-view-confirm"
            >
              Save view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
